import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import styled from 'styled-components';
import { 
  FiStar, 
  FiAward, 
  FiTrendingUp, 
  FiGift, 
  FiZap,  // Replaced FiSparkles with FiZap
  FiHeart,
  FiSun,
  FiCloud
} from 'react-icons/fi';

const ShowcaseSection = styled.section`
  padding: 8rem 0;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #d395ff, #ff7fd9, #b0ff64, transparent);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #b0ff64, #ff7fd9, #d395ff, transparent);
  }
`;

const ShowcaseContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 5rem;

  h2 {
    font-size: 4rem;
    font-weight: 800;
    color: white;
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 2px;

    span {
      background: linear-gradient(135deg, #d395ff, #ff7fd9, #b0ff64);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      font-size: 5rem;
      display: inline-block;
      transform: skew(-5deg);
    }

    @media (max-width: 768px) {
      font-size: 2.5rem;
      span {
        font-size: 3rem;
      }
    }
  }

  p {
    color: rgba(255, 255, 255, 0.6);
    font-size: 1.2rem;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const HaloGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-bottom: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const HaloItem = styled(motion.div)`
  position: relative;
  aspect-ratio: 3/4;
  overflow: hidden;
  cursor: pointer;
`;

const HaloImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: grayscale(30%) brightness(0.8);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  ${HaloItem}:hover & {
    filter: grayscale(0%) brightness(1);
    transform: scale(1.05);
  }
`;

const HaloOverlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(0, 0, 0, 0.2) 50%,
    rgba(0, 0, 0, 0.8) 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 2rem;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${HaloItem}:hover & {
    opacity: 1;
  }
`;

const HaloBadge = styled(motion.div)`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);
  padding: 0.4rem 1rem;
  border-radius: 30px;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: #0a0a0a;
  z-index: 2;

  svg {
    color: #d395ff;
  }
`;

const HaloTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.3rem;
  transform: translateY(20px);
  transition: transform 0.3s ease;

  ${HaloItem}:hover & {
    transform: translateY(0);
  }
`;

const HaloCategory = styled.span`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
  transform: translateY(20px);
  transition: transform 0.3s ease 0.1s;

  ${HaloItem}:hover & {
    transform: translateY(0);
  }
`;

const HaloDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 0.5rem;
  line-height: 1.4;
  transform: translateY(20px);
  transition: transform 0.3s ease 0.2s;

  ${HaloItem}:hover & {
    transform: translateY(0);
  }
`;

const IconMarquee = styled.div`
  margin-top: 5rem;
  padding: 2rem 0;
  background: rgba(255, 255, 255, 0.02);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transform: skewY(-2deg);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(211,149,255,0.1), 
      rgba(255,127,217,0.1), 
      rgba(176,255,100,0.1), 
      transparent
    );
    pointer-events: none;
  }
`;

const MarqueeTrack = styled(motion.div)`
  display: flex;
  white-space: nowrap;
  width: fit-content;
`;

const MarqueeItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  padding: 0 2rem;
  font-size: 1.2rem;
  color: white;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;

  svg {
    font-size: 1.8rem;
    color: ${props => {
      const colors = ['#d395ff', '#ff7fd9', '#b0ff64', '#83ffe3'];
      return colors[Math.floor(Math.random() * colors.length)];
    }};
    filter: drop-shadow(0 0 10px currentColor);
  }

  span {
    margin-right: 0.5rem;
  }
`;

const IconMarqueeReverse = styled(IconMarquee)`
  transform: skewY(2deg);
  margin-top: 1rem;
  background: rgba(255, 255, 255, 0.01);
`;

const ProductShowcase = ({ products = [] }) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const scale = useTransform(scrollYProgress, [0, 0.5], [0.9, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);

  // Halo products data
  const haloProducts = products.length > 0 ? products.slice(0, 5) : [
    {
      id: 1,
      name: "Big Bubble Pop",
      description: "Our signature Lollipop toffee with 5 fruity layers",
      category: "Signature",
      image: "/assets/product1.png",
      badge: "Best Seller",
      icon: <FiStar />
    },
    {
      id: 2,
      name: "Fruit Butter Fly",
      description: "Award-winning caramel toffee with Fruit infusion",
      category: "Award Winner",
      image: "/assets/product2.png",
      badge: "Award Winner",
      icon: <FiAward />
    },
    {
      id: 3,
      name: "Coconut Peda",
      description: "Coconut Toffee with the softness of Peda",
      category: "Luxury",
      image: "/assets/product4.png",
      badge: "Limited Edition",
      icon: <FiZap />  // Changed from FiSparkles
    },
    {
      id: 4,
      name: "Mango & Orange Candy",
      description: "Mixed toffee with real fruit pieces",
      category: "Fruity",
      image: "/assets/product3.png",
      badge: "Fan Favorite",
      icon: <FiGift />
    },
    {
      id: 5,
      name: "Fun Fruit & Fun Cool",
      description: "The two most famous bubblegum flavors reimagined",
      category: "Bubbly",
      image: "/assets/product5.png",
      badge: "New",
      icon: <FiSun />  // Changed from FiSparkles
    }
  ];

  // Icon sets for marquee - using only available icons
  const marqueeIcons = [
    { icon: <FiStar />, text: "BEST SELLER" },
    { icon: <FiAward />, text: "AWARD WINNER" },
    { icon: <FiTrendingUp />, text: "TRENDING" },
    { icon: <FiGift />, text: "GIFT READY" },
    { icon: <FiZap />, text: "HANDCRAFTED" },  // Changed
    { icon: <FiHeart />, text: "PREMIUM" },
    { icon: <FiAward />, text: "GOLD MEDAL" },
    { icon: <FiTrendingUp />, text: "TOP RATED" },
    { icon: <FiGift />, text: "SPECIAL EDITION" },
    { icon: <FiSun />, text: "ARTISAN" },  // Changed
    { icon: <FiCloud />, text: "DREAMY" },  // New
  ];

  // Duplicate for seamless loop
  const marqueeItems = [...marqueeIcons, ...marqueeIcons, ...marqueeIcons];
  const marqueeItemsReverse = [...marqueeIcons].reverse();

  return (
    <ShowcaseSection ref={sectionRef}>
      <ShowcaseContainer>
        <SectionHeader
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>
            The <span>Halo</span> Collection
          </h2>
          <p>Our crown jewels - five extraordinary flavors that define who we are</p>
        </SectionHeader>

        <HaloGrid>
          {haloProducts.map((product, index) => (
            <HaloItem
              key={product.id}
              style={{ scale, opacity }}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <HaloImage 
                src={product.image} 
                alt={product.name}
              />
              
              <HaloBadge
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                {product.icon}
                {product.badge}
              </HaloBadge>

              <HaloOverlay>
                <HaloTitle>{product.name}</HaloTitle>
                <HaloCategory>{product.category}</HaloCategory>
                <HaloDescription>{product.description}</HaloDescription>
              </HaloOverlay>
            </HaloItem>
          ))}
        </HaloGrid>
      </ShowcaseContainer>

      {/* Icon Marquee - Top */}
      <IconMarquee>
        <MarqueeTrack
          animate={{ x: [0, -2000] }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "linear",
            repeatType: "loop"
          }}
        >
          {marqueeItems.map((item, index) => (
            <MarqueeItem key={`marquee1-${index}`}>
              {item.icon}
              <span>{item.text}</span>
            </MarqueeItem>
          ))}
        </MarqueeTrack>
      </IconMarquee>

      {/* Icon Marquee - Bottom (Reverse Direction) */}
      <IconMarqueeReverse>
        <MarqueeTrack
          animate={{ x: [-2000, 0] }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
            repeatType: "loop"
          }}
        >
          {marqueeItemsReverse.map((item, index) => (
            <MarqueeItem key={`marquee2-${index}`}>
              {item.icon}
              <span>{item.text}</span>
            </MarqueeItem>
          ))}
        </MarqueeTrack>
      </IconMarqueeReverse>
    </ShowcaseSection>
  );
};

export default ProductShowcase;